import { k8sRequest, makeK8sCreator } from "../api";
import { formatAge } from "../format";
import { K8sContainerStatus, K8sPod, K8sPodList, PodContainerRow, PodDetail, PodRow } from "./types";

/**
 * "1/2" - ready containers over total, as `kubectl get pods` reports it.
 * This is often the first clue: a Running pod at 0/1 is not actually serving.
 */
const readyRatio = (pod: K8sPod): string => {
    const statuses = (pod.status && pod.status.containerStatuses) || [];
    const total = ((pod.spec && pod.spec.containers) || []).length || statuses.length;
    const ready = statuses.filter((status) => status.ready).length;

    return `${ready}/${total}`;
};

/** Restarts are summed across containers, matching kubectl. */
const totalRestarts = (pod: K8sPod): number =>
    ((pod.status && pod.status.containerStatuses) || []).reduce((sum, status) => sum + (status.restartCount || 0), 0);

/**
 * A container's state is a single-key object - {running:{}} / {waiting:{reason}} /
 * {terminated:{reason}}. Render the reason when there is one, because "waiting"
 * alone is useless and "waiting (CrashLoopBackOff)" is the answer.
 */
const containerState = (status?: K8sContainerStatus): string => {
    const state = (status && status.state) || {};
    const name = Object.keys(state)[0];

    if (!name) {
        return "";
    }

    const reason = state[name] && state[name].reason;

    return reason ? `${name} (${reason})` : name;
};

const toRow = (pod: K8sPod): PodRow => {
    const status = pod.status || {};
    const spec = pod.spec || {};

    return {
        // Pod names are unique only within a namespace.
        id: `${pod.metadata.namespace || "default"}/${pod.metadata.name}`,
        name: pod.metadata.name,
        namespace: pod.metadata.namespace || "",
        // A pod evicted or otherwise terminated carries the useful word in
        // `reason`; surface it instead of a bare "Failed".
        phase: status.reason ? `${status.phase} (${status.reason})` : status.phase || "",
        ready: readyRatio(pod),
        restarts: totalRestarts(pod),
        node: spec.nodeName || "",
        podIp: status.podIP || "",
        age: formatAge(pod.metadata.creationTimestamp),
    };
};

/** Flatten a PodList response into grid rows. */
export const parsePodList = (body: unknown): PodRow[] => {
    const list = (body || {}) as K8sPodList;

    return (list.items || []).map(toRow);
};

const toContainerRow = (pod: K8sPod, spec: { name: string; image?: string; resources?: any }): PodContainerRow => {
    const status = ((pod.status && pod.status.containerStatuses) || []).find((s) => s.name === spec.name);
    const resources = spec.resources || {};
    const requests = resources.requests || {};
    const limits = resources.limits || {};

    return {
        id: spec.name,
        name: spec.name,
        image: spec.image || "",
        state: containerState(status),
        ready: status && status.ready ? "Yes" : "No",
        restarts: (status && status.restartCount) || 0,
        cpuRequest: requests.cpu || "",
        memoryRequest: requests.memory || "",
        cpuLimit: limits.cpu || "",
        memoryLimit: limits.memory || "",
    };
};

/** Flatten a single Pod into everything the detail Overview renders. */
export const parsePod = (body: unknown): PodDetail | undefined => {
    const pod = body as K8sPod | undefined;

    if (!pod || !pod.metadata || !pod.metadata.name) {
        return undefined;
    }

    const status = pod.status || {};
    const containers = (pod.spec && pod.spec.containers) || [];

    return {
        name: pod.metadata.name,
        namespace: pod.metadata.namespace || "",
        phase: status.phase || "",
        reason: status.reason || "",
        message: status.message || "",
        node: (pod.spec && pod.spec.nodeName) || "",
        podIp: status.podIP || "",
        startTime: status.startTime || "",
        age: formatAge(pod.metadata.creationTimestamp),
        conditions: status.conditions || [],
        containers: containers.map((container) => toContainerRow(pod, container)),
        containerNames: containers.map((container) => container.name),
    };
};

const list = makeK8sCreator<PodRow[]>("k8sPods", parsePodList);
const detail = makeK8sCreator<PodDetail | undefined>("k8sPod", parsePod);

/** All pods, or - when a node is given - only the pods scheduled on that node. */
const getPodsAction = (nodeName?: string) =>
    list.createThunk(k8sRequest("api/v1/pods", nodeName ? { fieldSelector: `spec.nodeName=${nodeName}` } : undefined));

/** The pods belonging to a workload, found by the label selector it stamps on them. */
const getPodsBySelectorAction = (labelSelector: string, namespace = "default") =>
    list.createThunk(k8sRequest(`api/v1/namespaces/${encodeURIComponent(namespace)}/pods`, { labelSelector }));

const getPodAction = (namespace: string, name: string) =>
    detail.createThunk(
        k8sRequest(`api/v1/namespaces/${encodeURIComponent(namespace)}/pods/${encodeURIComponent(name)}`)
    );

export { getPodsAction, getPodsBySelectorAction, getPodAction };
export const reducer = list.reducer;
export const podDetailReducer = detail.reducer;
